import React from 'react';
import { Shield, Zap, Sun, Users, Clock, Leaf, Phone, Mail, CheckCircle } from 'lucide-react';
import gridsolar from './img/gridsolar.png';
import tank from './img/tank.png';
import cctvs from './img/cctvstate.png';
import solarHero from './img/solar-hero.png';

const AboutUs = () => {
  const stats = [
    { number: "40+", label: "Projects Completed" },
    { number: "500+", label: "Families Served" },
    { number: "1MW+", label: "Solar Installed" },
    { number: "75%", label: "Avg. Cost Reduction" },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#fafafa' }}>
      {/* Mission Statement */}
      <section className="py-14 md:py-20 px-6 md:px-10">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#712B35' }}>
            About Us
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-3" style={{ color: '#2a2118' }}>
            Our Mission
          </h2>
          <p className="text-base leading-relaxed mb-8 max-w-3xl mx-auto font-light" style={{ color: '#5a524c' }}>
            Torchbearer Technologies is dedicated to delivering innovative solutions in solar, CCTV, electrical, and HVAC. Our mission is to empower clients with reliable, sustainable, and cost-effective systems tailored for modern living.
          </p>
        </div>

        {/* Image Grid */}
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <div className="rounded-2xl overflow-hidden h-44 md:h-52">
            <img src={solarHero} alt="Solar installation" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
          </div>
          <div className="rounded-2xl overflow-hidden h-44 md:h-52">
            <img src={gridsolar} alt="Grid-tie solar system" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
          </div>
          <div className="rounded-2xl overflow-hidden h-44 md:h-52">
            <img src={cctvs} alt="CCTV installation" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
          </div>
          <div className="rounded-2xl overflow-hidden h-44 md:h-52">
            <img src={tank} alt="Water project" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
          </div>
        </div>

        {/* Stats */}
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="text-center p-5 rounded-2xl bg-white border border-gray-100">
              <div className="text-2xl md:text-3xl font-bold" style={{ color: '#712B35' }}>{s.number}</div>
              <div className="text-xs font-medium mt-1" style={{ color: '#5a524c' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-14 md:py-20 px-6 md:px-10 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#712B35' }}>
              Why Choose Us
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-3" style={{ color: '#2a2118' }}>
              Our Promise to You
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-2xl p-8 md:p-10 border border-gray-100 hover:shadow-xl transition-all duration-300" style={{ background: '#fafafa' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: '#712B35' }}>
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: '#2a2118' }}>24/7 Service</h3>
              <p className="leading-relaxed font-light text-sm" style={{ color: '#5a524c' }}>
                Our team is available round the clock for your service. We're committed to providing support whenever you need it.
              </p>
            </div>
            <div className="rounded-2xl p-8 md:p-10 border border-gray-100 hover:shadow-xl transition-all duration-300" style={{ background: '#fafafa' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: '#712B35' }}>
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: '#2a2118' }}>Reliable Solutions</h3>
              <p className="leading-relaxed font-light text-sm" style={{ color: '#5a524c' }}>
                We assure you the reduction in your monthly electricity bills while helping to save the environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-14 md:py-20 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#712B35' }}>
              Our Expertise
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-3" style={{ color: '#2a2118' }}>
              What We Do
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Sun className="w-6 h-6" />, title: "Solar Technologies", desc: "Panels, heating, pumping, and street lights" },
              { icon: <Shield className="w-6 h-6" />, title: "CCTV Systems", desc: "Security cameras and surveillance solutions" },
              { icon: <Zap className="w-6 h-6" />, title: "Electrical Services", desc: "Wiring, panels, and maintenance" },
              { icon: <Users className="w-6 h-6" />, title: "HVAC Systems", desc: "Climate control and ventilation" }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 transition-all duration-300" style={{ background: '#712B35' }}>
                  <div className="text-white">{item.icon}</div>
                </div>
                <h3 className="font-bold mb-2" style={{ color: '#2a2118' }}>{item.title}</h3>
                <p className="text-sm font-light" style={{ color: '#5a524c' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-14 md:py-16 px-6 md:px-10 overflow-hidden" style={{ background: '#712B35' }}>
        <div className="absolute inset-0 opacity-10">
          <img src={gridsolar} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Ready to Get Started?</h2>
          <p className="text-lg text-white/80 mb-6 font-light">We look forward to providing you our service.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0743076376" 
              className="bg-white px-8 py-3.5 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2"
              style={{ color: '#712B35' }}>
              <Phone className="w-5 h-5" />
              Call Now: 0743076376
            </a>
            <a href="mailto:torchbearertechnologies@gmail.com" 
              className="border-2 border-white text-white px-8 py-3.5 rounded-xl font-semibold transition-all duration-300 hover:bg-white hover:text-[#712B35] flex items-center justify-center gap-2">
              <Mail className="w-5 h-5" />
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
